/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { InjectionToken } from '@angular/core';
import { Point, Rectangle, Container, Graphics } from 'pixi.js-legacy';
import { default as PaperPoint } from '@appLegacy/lib/math/Point';
import { analyticsEventsService } from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';
import { first } from 'rxjs/operators';

import { DragAtEdge } from 'src/app/app-data/models/DragAtEdge';

import { Camera } from '@appCanvasCommon/core/Camera';

import { EventBinder } from './interaction-utils';
import { Component } from '@appCanvasCommon/component-system/Component';
import { ComponentInterface } from '@appCanvasCommon/interfaces/component.interface';
import { overlapRect } from 'utils/ticket-utils';

import { Interaction } from 'src/app/plain-javascript/services/interaction-blocker';
import { CanvasAngularLand } from 'src/app/app-data/interfaces/canvas-angular-land';
import { FancyGraphics } from '@appCanvasCommon/graphics/FancyGraphics';
import { PlanAccessCheck } from 'src/app/services/PlanAccessCheck';

export const SelectionBoxId = new InjectionToken('selectionBox');

export class SelectionBox extends Component implements ComponentInterface {
	private _binder: EventBinder;
	private graphics: Graphics;
	private parent: Container;

	private dragAtEdge: DragAtEdge;

	public started = false;

	private startPoint: Point;

	private planAccessCheck: PlanAccessCheck;

	constructor() {
		super();
	}

	public override setup(canvasAngularLand: CanvasAngularLand, parent: Container) {
		this.canvasAngularLand = canvasAngularLand;
		this.planAccessCheck = canvasAngularLand.planAccessCheck;
		this.parent = parent;
		this.graphics = new FancyGraphics();
		this._binder = new EventBinder(parent);

		this._binder.add('hammer-press', (evt) => this.start(evt.center));
		this._binder.add('hammer-holddragstart', (evt) => this.startDrag(evt.center));
		this._binder.add('hammer-holddrag', (evt) => this.drag(evt.center));
		this._binder.add('hammer-holddragend', (evt) => this.end(evt.center));
		this._binder.add('hammer-pressup', (evt) => this.end(evt.center));
	}

	public start(point: Point) {
		this.started = true;
		this.startPoint = point;
		this.parent.addChild(this.graphics);

		const camera = this.getParent().camera;
		this.dragAtEdge = new DragAtEdge(new Rectangle(0, 0, window.innerWidth, window.innerHeight));
		this.dragAtEdge.change$.subscribe((map) => {
			const revisedPoint = map.shiftedPoint;
			const z = camera.getScale().x;

			this.startPoint.x -= map.deltaX;
			this.startPoint.y -= map.deltaY;

			this.drag(map.lastPoint, true);

			this.canvasAngularLand.hud.onTranslate(new PaperPoint(-map.deltaX, -map.deltaY));
		});
	}
	public startDrag(point: Point) {
		if (this.started) {
			//remove plan
		} else {
			//remove this one
		}
	}

	public drag(point: Point, noDragAtEdge?: boolean) {
		if (!this.started) {
			return;
		}
		if (!noDragAtEdge) {
			this.dragAtEdge.update(point);
		}

		this.graphics.clear();
		const left = Math.min(this.startPoint.x, point.x);
		const right = Math.max(this.startPoint.x, point.x);
		const top = Math.min(this.startPoint.y, point.y);
		const bottom = Math.max(this.startPoint.y, point.y);

		this.getParent().run();
		this.graphics.beginFill(0x9c9c9c, 0.5);
		this.graphics.lineStyle(2, 0x616161);
		//it's _really_ absurd that drawRect doesn't accept a rectangle argument...
		this.graphics.drawRect(left, top, right - left, bottom - top);
	}

	public end(point: Point) {
		if (!this.started) {
			return;
		}
		this.dragAtEdge.cancel();

		const left = Math.min(this.startPoint.x, point.x);
		const right = Math.max(this.startPoint.x, point.x);
		const top = Math.min(this.startPoint.y, point.y);
		const bottom = Math.max(this.startPoint.y, point.y);
		const rect = new Rectangle(left, top, right - left, bottom - top);

		this.addToSelection(rect);

		this.started = false;
		this.graphics.clear();
		this.parent.removeChild(this.graphics);
	}

	public addToSelection(rect: Rectangle) {
		if (this.canvasAngularLand.tickets.editTicket$.getValue().size) {
			return;
		}
		const camera: Camera = this.getParent().camera;
		const worldRect: Rectangle = new Rectangle(
			camera.screenToRealX(rect.x),
			camera.screenToRealY(rect.y),
			rect.width / camera.scale.x,
			rect.height / camera.scale.y
		);
		const hitList = [];
		// _super_ lazy check, paste in the old shit
		this.canvasAngularLand.tickets.list$.pipe(first()).subscribe((tickets) => {
			tickets.forEach((ticket) => {
				if (
					ticket.hidden ||
					ticket.isFiltered ||
					this.planAccessCheck.hasRestrictedAccessSync ||
					!this.planAccessCheck.hasAccessToTicketSync(ticket)
				) {
					return;
				}
				const tRect = new Rectangle(ticket.view.left, ticket.view.top, ticket.view.width, ticket.view.height);
				if (overlapRect(worldRect, tRect)) {
					hitList.push(ticket);
				}
			});
		});
		if (
			hitList.length &&
			this.canvasAngularLand.interactionBlocker.check(Interaction.MultiSelect, { hasAccess: true })
		) {
			this.canvasAngularLand.actions.selectTicket(hitList);
			const planId = this.canvasAngularLand.route.snapshot.paramMap.get('planId');
			analyticsEventsService.multiselectRegion(hitList.length, planId);
		}
	}

	public override destroy() {
		this._binder.destroy();
		this.graphics.destroy();
		this.parent = null;
	}
}
